import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, CheckSquare, Square, FloppyDisk, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CAMPAIGN_AUDIENCES, CAMPAIGN_CHANNELS } from "@/lib/constants";
import { PageHeader, SectionTitle, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AUD_KEYS = {
  dormant_branches: "dormant_branches",
  empanelment_renewals: "empanelment_renewals",
  new_branches: "new_branches_in_region",
  past_clients: "referral_clients",
};
const TEMPLATE_KINDS = {
  new_branch_intro: "New Branch Introduction",
  empanelment_renewal: "Empanelment Renewal Outreach",
  reengage_dormant: "Re-engage Dormant Branch",
  festival_greeting: "Festival / Relationship Greeting",
  feedback_request: "Post-Job Feedback Request",
};
const TONES = ["professional", "warm", "concise", "persuasive"];

export default function NewCampaign() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: "", audience: "dormant_branches", template_kind: "reengage_dormant", tone: "professional", channel: "email", scheduled_for: "", extra_context: "", message: "" });
  const [selected, setSelected] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/outreach/leads").then((r) => setLeads(r.data)).catch(() => setLeads({}));
    api.get("/coll/outreach_templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const pool = leads ? (leads[AUD_KEYS[form.audience]] || []) : [];
  const labelOf = (x) => (x.bank_name ? `${x.bank_name} — ${x.branch_name}` : x.name);
  const contactOf = (x) => x.contact_person || x.name || "";

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((y) => y !== id) : [...s, id]));

  const draft = async () => {
    if (!form.name) { toast.error("Give the campaign a name first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/campaign-draft", {
        campaign_name: form.name, audience: form.audience,
        template_kind: form.template_kind, tone: form.tone, extra_context: form.extra_context,
      });
      setForm((f) => ({ ...f, message: data.draft }));
      toast.success("Draft ready — edit it as you like");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDrafting(false); }
  };

  const applyTemplate = (t) => {
    const body = t.channel === "email" && t.subject ? `Subject: ${t.subject}\n\n${t.body}` : t.body;
    setForm((f) => ({ ...f, message: body, channel: t.channel || f.channel, template_kind: t.kind || f.template_kind }));
    toast.success(`Loaded "${t.name}"`);
  };

  const save = async (status) => {
    if (!form.name) { toast.error("Give the campaign a name"); return; }
    if (selected.length === 0) { toast.error("Pick at least one recipient"); return; }
    setSaving(true);
    try {
      const recipients = pool.filter((x) => selected.includes(x.id)).map((x) => ({
        id: x.id, label: labelOf(x), contact: contactOf(x),
        phone: x.phone || "", email: x.email || "", status: "no_response",
      }));
      await api.post("/coll/campaigns", { ...form, status, recipients, scheduled_for: form.scheduled_for || null });
      toast.success(status === "active" ? "Campaign launched" : "Campaign saved as draft");
      navigate("/app/outreach");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!leads) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading audiences…</div>;

  return (
    <div className="space-y-6" data-testid="new-campaign-page">
      <PageHeader overline="AI Outreach" title="Create New Campaign" subtitle="Pick an audience, let AI draft the copy, then launch" />

      <Card className="p-6 border-border space-y-4">
        <SectionTitle>1. Campaign basics</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Campaign Name *</Label><Input value={form.name} onChange={set("name")} placeholder="e.g. Aug 2026 dormant branch revival" data-testid="campaign-name" /></div>
          <div className="space-y-1.5"><Label>Audience</Label>
            <Select value={form.audience} onValueChange={(v) => { setForm({ ...form, audience: v }); setSelected([]); }}>
              <SelectTrigger data-testid="campaign-audience"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CAMPAIGN_AUDIENCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Channel</Label>
            <Select value={form.channel} onValueChange={set("channel")}>
              <SelectTrigger data-testid="campaign-channel"><SelectValue /></SelectTrigger>
              <SelectContent>{CAMPAIGN_CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Schedule (optional)</Label><DatePicker value={form.scheduled_for} onChange={set("scheduled_for")} placeholder="Select date" clearable className="w-full" data-testid="campaign-schedule" /></div>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle>2. Choose recipients <span className="text-xs font-normal text-muted-foreground">({selected.length} of {pool.length} selected)</span></SectionTitle>
        {pool.length === 0 ? (
          <EmptyState>No matches for this audience yet. Try another audience, or add empanelment renewal dates to your branches.</EmptyState>
        ) : (
          <>
            <div className="flex gap-2 mb-3">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(pool.map((x) => x.id))} data-testid="select-all-btn">Select all</Button>
              <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => setSelected([])} data-testid="clear-all-btn">Clear</Button>
            </div>
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
              {pool.map((x) => {
                const on = selected.includes(x.id);
                return (
                  <button key={x.id} onClick={() => toggle(x.id)} data-testid={`recipient-${x.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-md border text-left transition-colors duration-150 ${on ? "bg-accent/10 border-accent/40" : "bg-muted border-border hover:border-accent/40"}`}>
                    {on ? <CheckSquare size={18} weight="fill" className="text-accent shrink-0" /> : <Square size={18} className="text-muted-foreground shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-primary truncate">{labelOf(x)}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {contactOf(x) || "No contact person"}{x.phone ? ` · ${x.phone}` : ""}
                        {x.last_job_date ? ` · last job ${x.last_job_date}` : ""}
                        {x.empanelment_renewal_due ? ` · renewal ${x.empanelment_renewal_due.slice(0, 10)}` : ""}
                        {x.job_count ? ` · ${x.job_count} past jobs` : ""}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </Card>

      <Card className="p-6 border-border space-y-4">
        <SectionTitle icon={Sparkle}>3. The message</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Template / Purpose</Label>
            <Select value={form.template_kind} onValueChange={set("template_kind")}>
              <SelectTrigger data-testid="campaign-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TEMPLATE_KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tone</Label>
            <Select value={form.tone} onValueChange={set("tone")}>
              <SelectTrigger data-testid="campaign-tone"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5"><Label>Extra context for the AI (optional)</Label><Input value={form.extra_context} onChange={set("extra_context")} placeholder="e.g. We now offer 48-hour turnaround in Raipur city" data-testid="campaign-context" /></div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={draft} disabled={drafting} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="ai-draft-btn">
            {drafting ? <ArrowClockwise size={16} className="mr-1 animate-spin" /> : <Sparkle size={16} weight="fill" className="mr-1" />}
            {drafting ? "Drafting…" : "Draft with AI"}
          </Button>
          {templates.map((t) => (
            <Button key={t.id} size="sm" variant="outline" className="rounded-full" onClick={() => applyTemplate(t)} data-testid={`use-template-${t.id}`}>{t.name}</Button>
          ))}
        </div>

        <div className="space-y-1.5">
          <Label>Message</Label>
          <Textarea rows={10} value={form.message} onChange={set("message")} placeholder="Your outreach message. Use {{name}} and {{branch}} as merge placeholders." className="font-sans text-sm" data-testid="campaign-message" />
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => save("draft")} disabled={saving} variant="outline" className="rounded-full" data-testid="save-draft-btn">
          <FloppyDisk size={16} className="mr-1" /> Save as Draft
        </Button>
        <Button onClick={() => save("active")} disabled={saving} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="launch-campaign-btn">
          {saving ? "Saving…" : "Launch Campaign"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Launching records the campaign and its recipients so you can track replies. Messages are sent from your own email or WhatsApp — copy the text from the campaign card.</p>
    </div>
  );
}
